/**
 * DEVLOGS-TEMP — the module-level singleton and the always-safe `devlog` facade.
 *
 * A module-level store rather than props or context, for the reason spelled out
 * in lib/player-shell/src/player-telemetry.ts: the emitters are a playback
 * engine, a media-cache path and a watchdog timer, and threading an optional
 * callback through every one of them is how this ends up wired on one shell and
 * silently missing on the other.
 *
 * PRE-INIT BUFFER — not a nicety, a correctness requirement on web.
 * artifacts/web/src/player/player-runtime.tsx registers the service worker as a
 * MODULE-BODY side effect, and ES module bodies of imports evaluate before the
 * importer's body. So `registerServiceWorker()` runs before main.tsx can call
 * init. Calls made before configuration go into a bounded ring and are replayed
 * on configure — the same trick player-debug.ts uses with its 120-entry buffer.
 */

import type { DevLogger, DevLogSpan } from "./logger.ts";
import { LEVELS, type LevelName } from "./level.ts";
import type { BatcherStats } from "./batcher.ts";
import type { FlushReason } from "./batcher.ts";
import type { Level, RecordExtras } from "./types.ts";

interface PendingCall {
  lvl: Level;
  ns: string;
  evt: string;
  data?: Record<string, unknown>;
  extra?: RecordExtras;
}

const PENDING_CAP = 200;

let impl: DevLogger | null = null;
const pending: PendingCall[] = [];

function remember(call: PendingCall): void {
  if (pending.length >= PENDING_CAP) pending.shift();
  pending.push(call);
}

export function configureDevLog(next: DevLogger | null): void {
  impl = next;
  if (next === null) {
    pending.length = 0;
    return;
    }
  // Replay anything captured before boot finished, oldest first.
  const replay = pending.splice(0, pending.length);
  for (const call of replay) {
    try {
      next.log(call.lvl, call.ns, call.evt, call.data, call.extra);
    } catch {
      /* fire-and-forget */
    }
  }
}

export function getDevLog(): DevLogger | null {
  return impl;
}

export function isDevLogEnabled(): boolean {
  return impl !== null;
}

/** Test seam. */
export function resetDevLog(): void {
  impl = null;
  pending.length = 0;
}

const NOOP_STATS: BatcherStats = Object.freeze({
  queued: 0,
  bytes: 0,
  dropped: 0,
  sent: 0,
  failed: 0,
  batches: 0,
  inFlight: false,
  degraded: false,
  disabled: false,
  lastFailureAt: null,
});

const NOOP_SPAN: DevLogSpan = Object.freeze({
  id: "",
  end() {
    /* no-op */
  },
  fail() {
    /* no-op */
  },
});

function toLevel(level: Level | LevelName): Level {
  return typeof level === "number" ? level : (LEVELS[level] ?? LEVELS.info);
}

function facade(prefix: string): DevLogger {
  const scoped = (ns: string): string => (prefix.length > 0 ? `${prefix}.${ns}` : ns);

  const dispatch = (
    lvl: Level,
    ns: string,
    evt: string,
    data?: Record<string, unknown>,
    extra?: RecordExtras,
  ): void => {
    try {
      if (impl !== null) {
        impl.log(lvl, scoped(ns), evt, data, extra);
        return;
      }
      remember({ lvl, ns: scoped(ns), evt, data, extra });
    } catch {
      /* fire-and-forget */
    }
  };

  return {
    log(level, ns, evt, data, extra) {
      dispatch(toLevel(level), ns, evt, data, extra);
    },
    trace(ns, evt, data, extra) {
      dispatch(LEVELS.trace, ns, evt, data, extra);
    },
    debug(ns, evt, data, extra) {
      dispatch(LEVELS.debug, ns, evt, data, extra);
    },
    info(ns, evt, data, extra) {
      dispatch(LEVELS.info, ns, evt, data, extra);
    },
    warn(ns, evt, data, extra) {
      dispatch(LEVELS.warn, ns, evt, data, extra);
    },
    error(ns, evt, err, data, extra) {
      dispatch(LEVELS.error, ns, evt, data, { ...extra, err: err ?? extra?.err });
    },
    child(ns) {
      return facade(scoped(ns));
    },
    span(ns, evt, data) {
      try {
        return impl !== null ? impl.span(scoped(ns), evt, data) : NOOP_SPAN;
      } catch {
        return NOOP_SPAN;
      }
    },
    enabled(ns, level) {
      try {
        return impl !== null ? impl.enabled(scoped(ns), level) : false;
      } catch {
        return false;
      }
    },
    flush(reason?: FlushReason) {
      try {
        impl?.flush(reason);
      } catch {
        /* fire-and-forget */
      }
    },
    stats() {
      try {
        return impl?.stats() ?? NOOP_STATS;
      } catch {
        return NOOP_STATS;
      }
    },
  };
}

/**
 * The always-safe entry point. A real object that no-ops until configured, so
 * call sites write `devlog.debug(...)` with no null check and no import-order
 * hazard, and an unconfigured shell pays one `if` per call.
 */
export const devlog: DevLogger = facade("");
